import type { EpochInfo } from '~/utils/api'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { EPOCH_RELOAD_INTERVAL } from '~/config'
import { useApp } from '~/hooks/app'
import { useEmitter } from '~/hooks/emitter'
import { useClusterStore } from '~/store/cluster'
import { useConnectionStore } from '~/store/connection'
import { getEpochInfo } from '~/utils/api'

export const EPOCH_UPDATE_EVENT = Symbol('EPOCH_UPDATE_EVENT')

export const useEpochStore = defineStore('epoch', () => {
  const emitter = useEmitter()
  const connectionStore = useConnectionStore()
  const clusterStore = useClusterStore()

  const { isClient } = useApp()

  const stakePoolAddress = computed(() => connectionStore.stakePoolAddress)
  const hourlySlotTime = computed(() => clusterStore.hourlySlotTime)

  const epochInfo = ref<EpochInfo>()
  const epochTimeRemaining = ref(0)
  const epochNumber = ref(0)
  const epochTimeTotal = ref(0)
  const loading = ref(true)

  const epochProgress = computed(() => {
    if (!epochInfo.value) {
      return 0
    }
    return ((100 * epochInfo.value.slotIndex) / epochInfo.value.slotsInEpoch)?.toFixed(1)
  })

  const loadEpochInfo = async () => {
    try {
      if (!isClient.value) {
        return
      }
      loading.value = true
      epochInfo.value = await getEpochInfo(connectionStore.network)
    } finally {
      loading.value = false
    }
  }

  const update = useDebounceFn(
    () => Promise.all([loadEpochInfo(), clusterStore.calcHourlySlotTime()]),
    300,
  )

  watch(epochInfo, (e) => {
    const timeRemaining = e ? (e.slotsInEpoch - e.slotIndex) * (e.hourlySlotTime ?? hourlySlotTime.value) : 0
    const differenceInMinutes = Math.abs(timeRemaining - epochTimeRemaining.value) / 1000 * 60

    if (differenceInMinutes >= 10 || !epochTimeRemaining.value) {
      epochTimeRemaining.value = timeRemaining
    }

    epochTimeTotal.value = e ? e.slotsInEpoch * hourlySlotTime.value : 0
    if (e && e.epoch !== epochNumber.value) {
      epochNumber.value = e.epoch
    }
  })

  watch(stakePoolAddress, (next, prev) => {
    if (next?.toBase58() !== prev?.toBase58()) {
      update()
    }
  }, { immediate: true })

  onMounted(() => {
    if (import.meta.env.SSR) {
      return
    }
    // Re-sync epoch info
    setInterval(async () => {
      await update()
      emitter.emit(EPOCH_UPDATE_EVENT, epochInfo.value)
      console.log('Reload epoch info', epochInfo.value)
    }, EPOCH_RELOAD_INTERVAL)

    // Update timer
    setInterval(async () => {
      const newVal = epochTimeRemaining.value - 1000
      epochTimeRemaining.value = Math.max(newVal, 0)
    }, 1000)
  })

  return {
    loading,
    epochInfo,
    epochNumber,
    epochTimeRemaining,
    epochProgress,
    epochTimeTotal,
    reload: () => loadEpochInfo(),
  }
})
